import type { Vec2 } from './types.ts';

export function clamp(value: number, min: number, max: number): number {
  if (min > max) {
    throw new Error('clamp min must be less than or equal to max');
  }

  return Math.min(max, Math.max(min, value));
}

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(vector: Vec2, factor: number): Vec2 {
  return { x: vector.x * factor, y: vector.y * factor };
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

export function lengthSq(vector: Vec2): number {
  return vector.x * vector.x + vector.y * vector.y;
}

export function length(vector: Vec2): number {
  return Math.sqrt(lengthSq(vector));
}

/**
 * Smallest angle between a travel direction and a crossed line, in degrees [0, 90].
 * 0° is a graze (parallel to the shore); 90° is head-on (perpendicular).
 */
export function crossingAngleDeg(approach: Vec2, edge: Vec2): number | null {
  const approachLen = length(approach);
  const edgeLen = length(edge);

  if (approachLen < 1e-9 || edgeLen < 1e-9) {
    return null;
  }

  const aligned = Math.abs(dot(approach, edge) / (approachLen * edgeLen));
  return (Math.acos(clamp(aligned, 0, 1)) * 180) / Math.PI;
}

/**
 * Reflects `approach` off `edge` the way a ball leaves a wall: same angle, other side.
 * The water-facing normal is the one that faces back along the incoming path.
 */
export function bounceDirection(approach: Vec2, edge: Vec2): Vec2 | null {
  const approachLen = length(approach);
  const edgeLen = length(edge);

  if (approachLen < 1e-9 || edgeLen < 1e-9) {
    return null;
  }

  const nA = normalize({ x: -edge.y, y: edge.x });
  const towardWater = { x: -approach.x, y: -approach.y };
  const normal = dot(nA, towardWater) > 0 ? nA : { x: -nA.x, y: -nA.y };

  return sub(approach, scale(normal, 2 * dot(approach, normal)));
}

export function fromAngle(heading: number, magnitude = 1): Vec2 {
  return {
    x: Math.cos(heading) * magnitude,
    y: Math.sin(heading) * magnitude,
  };
}

export function toAngle(vector: Vec2): number {
  return Math.atan2(vector.y, vector.x);
}

export function normalize(vector: Vec2): Vec2 {
  const magnitudeSq = lengthSq(vector);

  if (magnitudeSq === 0) {
    return { x: 0, y: 0 };
  }

  const magnitude = Math.sqrt(magnitudeSq);

  return { x: vector.x / magnitude, y: vector.y / magnitude };
}

export function headingUnit(heading: number): Vec2 {
  return fromAngle(heading, 1);
}

/** Shortest signed rotation from one angle to another, in `(-PI, PI]`. */
export function angleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

export function shortestTargetRotation(
  current: number,
  target: number,
): number {
  return current + angleDelta(current, target);
}

/** Signed area of the parallelogram spanned by `(b - a)` and `(c - a)`; sign gives turn direction. */
function cross(a: Vec2, b: Vec2, c: Vec2): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** True if `point` lies on the closed bounding box of segment `a`-`b` (only meaningful when collinear). */
function isOnSegmentBounds(a: Vec2, b: Vec2, point: Vec2): boolean {
  return (
    Math.min(a.x, b.x) <= point.x &&
    point.x <= Math.max(a.x, b.x) &&
    Math.min(a.y, b.y) <= point.y &&
    point.y <= Math.max(a.y, b.y)
  );
}

/** Standard orientation-based 2D segment intersection test, including touching/collinear cases. */
export function segmentsIntersect(a1: Vec2, a2: Vec2, b1: Vec2, b2: Vec2): boolean {
  const d1 = cross(b1, b2, a1);
  const d2 = cross(b1, b2, a2);
  const d3 = cross(a1, a2, b1);
  const d4 = cross(a1, a2, b2);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  if (d1 === 0 && isOnSegmentBounds(b1, b2, a1)) {
    return true;
  }

  if (d2 === 0 && isOnSegmentBounds(b1, b2, a2)) {
    return true;
  }

  if (d3 === 0 && isOnSegmentBounds(a1, a2, b1)) {
    return true;
  }

  if (d4 === 0 && isOnSegmentBounds(a1, a2, b2)) {
    return true;
  }

  return false;
}

/**
 * First point along `a1`→`a2` that lies on `b1`→`b2`, or `null` when the segments miss.
 * Collinear overlaps return the candidate closest to `a1`.
 */
export function segmentIntersection(
  a1: Vec2,
  a2: Vec2,
  b1: Vec2,
  b2: Vec2,
): Vec2 | null {
  const ax = a2.x - a1.x;
  const ay = a2.y - a1.y;
  const bx = b2.x - b1.x;
  const by = b2.y - b1.y;
  const denom = ax * by - ay * bx;

  if (Math.abs(denom) < 1e-12) {
    if (!segmentsIntersect(a1, a2, b1, b2)) {
      return null;
    }

    const candidates = [a1, b1, b2, a2].filter(
      (point) => isOnSegmentBounds(a1, a2, point) && isOnSegmentBounds(b1, b2, point),
    );
    let best: Vec2 | null = null;
    let bestAlong = Number.POSITIVE_INFINITY;

    for (const point of candidates) {
      const along = lengthSq(sub(point, a1));

      if (along < bestAlong) {
        best = point;
        bestAlong = along;
      }
    }

    return best ? { x: best.x, y: best.y } : null;
  }

  const t = ((b1.x - a1.x) * by - (b1.y - a1.y) * bx) / denom;
  const u = ((b1.x - a1.x) * ay - (b1.y - a1.y) * ax) / denom;

  if (t < 0 || t > 1 || u < 0 || u > 1) {
    return null;
  }

  return { x: a1.x + t * ax, y: a1.y + t * ay };
}
